/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Column Personalization Utils
 */
import _ from 'lodash';
import { ColDef } from 'ag-grid-community';

import { MAX_CUSTOM_FIELD_COLUMNS } from 'app-constants/admin/customAttribute';

const COLUMN_IDS = {
	title: 'title',
	actions: 'actions',
};

const COLUMN_ORDERS = {
	PINNED_LEFT: 0,
	DEFAULT: 1,
	HIDDEN: 2,
	PINNED_RIGHT: 3,
};

const TABLE_OFFSET = {
	invoices: 325,
	auditLogs: 165,
	claimStatusRequests: 290,
	draftClaims: 290,
	archivedClaims: 290,
	eligibilityRequests: 290,
	eligibilityBatchRequests: 290,
	allWorkLists: 290,
	era: 290,
	enrollment: 365,
	selectedPayers: 210,
	providers: 210,
	userManagement: 210,
	selectedOnboardingPayers: 665,
	smallTable: 450,
	patients: 420,
	patientResponseAccounts: 390,
	accounts: 290,
	accountClaims: 250,
	addClaimsToManualWorklist: 450,
	worklistResponseClaims: 390,
	allPayments: 275,
	encounters: 350,
	patientEligibility: 350,
};

const TABLE_META = {
	eligibilityRequests: {
		personalizationKey: '/eligibility/responses',
		tableOffset: TABLE_OFFSET.eligibilityRequests,
	},
	claimStatusRequests: {
		personalizationKey: '/claims/responses',
		tableOffset: TABLE_OFFSET.claimStatusRequests,
	},
	draftClaims: {
		personalizationKey: '/claims/responses/draft',
		tableOffset: TABLE_OFFSET.draftClaims,
	},
	archivedClaims: {
		personalizationKey: '/claims/responses/archived',
		tableOffset: TABLE_OFFSET.archivedClaims,
	},
	allWorkLists: {
		personalizationKey: '/worklists/all',
		tableOffset: TABLE_OFFSET.allWorkLists,
	},
	worklistResponseClaims: {
		personalizationKey: '/worklists/response/claims',
		tableOffset: TABLE_OFFSET.worklistResponseClaims,
	},
	era: {
		personalizationKey: '/claims/era-list',
		tableOffset: TABLE_OFFSET.era,
	},
	patients: {
		tableOffset: TABLE_OFFSET.patients,
	},
	patientAccounts: {
		personalizationKey: '/patients/accounts',
		tableOffset: TABLE_OFFSET.patientResponseAccounts,
	},
	patientCharges: {
		personalizationKey: '/patients/charges',
	},
	accounts: {
		personalizationKey: '/accounts/all',
		tableOffset: TABLE_OFFSET.accounts,
	},
	accountClaims: {
		personalizationKey: '/accounts/details',
		tableOffset: TABLE_OFFSET.accountClaims,
	},
	allPayments: {
		personalizationKey: '/payments/all',
		tableOffset: TABLE_OFFSET.allPayments,
	},

	eraClaims: {
		personalizationKey: '/era/claims',
	},
	encounters: {
		personalizationKey: '/patients/encounters',
		tableOffset: TABLE_OFFSET.encounters,
	},
	patientEligibility: {
		personalizationKey: '/patients/eligibility',
		tableOffset: TABLE_OFFSET.patientEligibility,
	},
};

const DEFAULT_COLUMN_META: IColumnMeta[] = [
	{
		colId: COLUMN_IDS.title,
		pinned: 'left',
	},
	{
		colId: COLUMN_IDS.actions,
		pinned: 'right',
	},
];

const isTitleColumn = (colId: string): boolean => colId === COLUMN_IDS.title;
const isActionColumn = (colId: string): boolean => colId === COLUMN_IDS.actions;
const isSpecialColumn = (colId: string): boolean => isTitleColumn(colId) || isActionColumn(colId);
const isPinnedLeft = (item: IColumnMeta): boolean => item?.pinned === 'left';
const isPinnedRight = (item: IColumnMeta): boolean => item?.pinned === 'right';

/**
 * prepareColDef Params
 */
type IParams = { colDef: ColDef[]; columnMeta: IColumnMeta[] };

/**
 * Prepare ColDef and ColumnMeta for ag-grid
 * @param {IParams} params
 * @returns {IParams} prepared ColDef and ColumnMeta
 */
const prepareColDef = (params: IParams): IParams => {
	const initialOrder = _.isEmpty(params.columnMeta) ? DEFAULT_COLUMN_META : params.columnMeta;
	const prepared = _.orderBy(initialOrder, (item) => {
		if (isPinnedLeft(item)) {
			return COLUMN_ORDERS.PINNED_LEFT;
		}
		if (isPinnedRight(item)) {
			return COLUMN_ORDERS.PINNED_RIGHT;
		}
		if (item?.hide) {
			return COLUMN_ORDERS.HIDDEN;
		}
		return COLUMN_ORDERS.DEFAULT;
	});

	const orderedColumns: ColDef[] = [];
	const orderedColumnMeta: IColumnMeta[] = [];
	const newColumns: ColDef[] = [];
	const newColumnMeta: IColumnMeta[] = [];
	let rightColumn: ColDef;
	let rightColumnMeta: IColumnMeta;

	_.forEach(params.colDef, (column) => {
		const isTitle = isTitleColumn(column.colId);
		const isAction = isActionColumn(column.colId);
		const item = _.find(prepared, { colId: column.colId });
		const pinToRight = isAction && isPinnedRight(item);
		let itemOrder = _.findIndex(prepared, { colId: column.colId });
		itemOrder = pinToRight ? 50 : itemOrder;
		const isNewColumn = itemOrder < 0;
		const isCustomField = !!(column as unknown as AnyType)?.isCustomField;
		let colDefItem = {
			colId: column.colId,
			hide: !isTitle && !isAction && (item?.hide || false),
			pinned: item?.pinned || false,
		};
		const metaItem: IColumnMeta = {
			...colDefItem,
			title: column.headerName || column.colId,
			...(isCustomField && { isCustomField: true }),
		};
		colDefItem = { ...column, ...colDefItem };

		// pin action to right
		if (isAction && isPinnedRight(item)) {
			rightColumn = colDefItem;
			rightColumnMeta = metaItem;
		}
		// static or custom attribute column with no preferred order
		else if (isNewColumn) {
			newColumns.push(colDefItem);
			newColumnMeta.push(metaItem);
		}
		// default
		else {
			_.setWith(orderedColumns, itemOrder, colDefItem);
			_.setWith(orderedColumnMeta, itemOrder, metaItem);
		}
	});

	const finalColDef = _.compact(_.concat(orderedColumns, newColumns, rightColumn));
	const finalColumnMeta = _.compact(_.concat(orderedColumnMeta, newColumnMeta, rightColumnMeta));

	// enforce custom field visibility limit
	let visibleCustomFieldCount = 0;
	_.forEach(finalColumnMeta, (meta, index) => {
		if (meta?.isCustomField && !meta.hide) {
			visibleCustomFieldCount++;
			if (visibleCustomFieldCount > MAX_CUSTOM_FIELD_COLUMNS) {
				finalColumnMeta[index] = { ...meta, hide: true };
				finalColDef[index] = { ...finalColDef?.[index], hide: true };
			}
		}
	});

	return {
		colDef: finalColDef,
		columnMeta: finalColumnMeta,
	};
};

/**
 * Prepare Submit Model
 */
const prepareSubmitModel = (columnMeta: IColumnMeta[]): IColumnMeta[] => {
	return _.map(columnMeta, (item) => _.omitBy(item, (attr, key) => !attr || key === 'title' || key === 'isCustomField') as IColumnMeta);
};

export {
	COLUMN_IDS,
	TABLE_OFFSET,
	DEFAULT_COLUMN_META,
	TABLE_META,
	COLUMN_ORDERS,
	isSpecialColumn,
	prepareColDef,
	prepareSubmitModel,
	isPinnedLeft,
	isPinnedRight,
	isTitleColumn,
	isActionColumn,
};
